import { useConfigurationStore } from '@stores'
import { GameUIMessageType, ViewportType } from 'game-ui-types'
import React, { useCallback, useEffect, useState } from 'react'

/**
 * ScaledWrapper Component
 *
 * Dynamically scales and centers child elements based on viewport dimensions while maintaining the aspect ratio.
 * Supports different viewport types: mobile portrait, mobile landscape, tablet, and desktop.
 */
interface ScaledWrapperProps {
    className?: string
    children: React.ReactNode
    onUpdateScale?: (scale: number) => void
}

const ScaledWrapper: React.FC<ScaledWrapperProps> = ({ className, children, onUpdateScale }) => {
    const setConfiguration = useConfigurationStore((state) => state.setConfiguration)
    const isHistoryMode = useConfigurationStore((state) => state.isHistoryMode)
    const isReplayMode = useConfigurationStore((state) => state.isReplayMode)
    const stageDimensions = useConfigurationStore((state) => state.stageDimensions)
    const [scale, setScale] = useState<number>(1)
    const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null)

    const updateScale = (s: number) => {
        setScale(s)
        onUpdateScale?.(s)
    }

    /**
     * Detect if the device is an iPad
     * Uses a combination of user agent and touch capability detection.
     */

    // comment out the iPad detection as it is not used in the current implementation
    // const isIpad = useCallback(() => {
    //     const userAgent = navigator.userAgent || ''
    //     const isMac = /Macintosh/i.test(userAgent)
    //     const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 1

    //     return (isMac && isTouchDevice) || /iPad/i.test(userAgent)
    // }, [])

    /**
     * Detect the device mode and set the appropriate viewport dimensions.
     */
    const detectDeviceMode = useCallback(() => {
        const library = window.egtLibrary || window.amusnetLibrary

        const isMobile: boolean = library?.Device.isMobile
        const orientationType: string | undefined = library?.Device.currentOrientation

        if (isMobile) {
            if (orientationType?.includes('portrait')) {
                setConfiguration({ viewport: ViewportType.MOBILE_PORTRAIT })

                return stageDimensions[ViewportType.MOBILE_PORTRAIT]
            } else if (orientationType?.includes('landscape')) {
                setConfiguration({ viewport: ViewportType.MOBILE_LANDSCAPE })

                return stageDimensions[ViewportType.MOBILE_LANDSCAPE]
            }
        }

        // Fallback to desktop
        setConfiguration({ viewport: ViewportType.DESKTOP })

        return stageDimensions[ViewportType.DESKTOP]
    }, [setConfiguration])

    /**
     * Handle viewport resizing and orientation changes.
     * Ensures correct scaling and dimension updates.
     */
    const handleResize = useCallback(() => {
        const newDimensions = detectDeviceMode()

        const { innerWidth, innerHeight } = window
        const scaleWidth = innerWidth / newDimensions.width
        const scaleHeight = innerHeight / newDimensions.height
        const calculatedScale = Math.min(scaleWidth, scaleHeight)

        setDimensions(newDimensions)
        updateScale(calculatedScale)
    }, [detectDeviceMode])

    // Initial setup and resize listener
    useEffect(() => {
        handleResize()

        window.addEventListener('resize', handleResize)
        window.addEventListener('orientationchange', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleResize)
        }
    }, [handleResize, isHistoryMode, isReplayMode])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const { type } = event.data

            if (type === GameUIMessageType.SETUP) {
                handleResize()
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [])

    return (
        <div
            className={className}
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                width: `${dimensions?.width ?? 0}px`,
                height: `${dimensions?.height ?? 0}px`,
                transform: `translate(-50%, -50%) scale(${scale})`,
                transformOrigin: 'center',
                opacity: dimensions ? 1 : 0, // Use opacity if device details not loaded yet to avoid mispositioned artifacts
            }}
        >
            {children}
        </div>
    )
}

export default ScaledWrapper
